import React, { Component } from "react";
import PropTypes from "prop-types";
import { SitePreview } from "../SitePreview";
import classNames from "classnames";
import { t } from "i18next";
import { urlParmsToPromo } from "../../containers/Editor";
import jiffylubelogo from "../../assets/logo-black.png";
import barcode from "../../assets/barcode-example.png";
import Moment from "react-moment";
import makeImagesResponsive from "../../assets/responsive-img.min.js";

class Preview extends Component {
  static propTypes = {
    className: PropTypes.string,
    promo: PropTypes.object,
    box: PropTypes.oneOf(["a", "b", "c", "alert"]),
    mockup: PropTypes.string,
  };

  static defaultProps = {
    promo: null,
    box: "a",
    mockup: "true",
  };

  constructor(props) {
    super(props);
    this.handleUpdatePromoPreview = this.handleUpdatePromoPreview.bind(this);

    let promo = null;
    if (this.props.location) {
      promo = urlParmsToPromo(this.props.location.search);
      promo.layout = promo.layout || "layout1";
      promo.heading = promo.heading || "Start here with " + promo.layout;
      promo.subheading = promo.subheading || "See Location for Details";
      promo.design_option = promo.subheading || "plug_n_play";
      promo.tag_text = promo.tag_text || "Ends Soon";
      promo.button_text = promo.button_text || "Learn More";
    }
    this.state = {
      promo,
    };
  }

  componentDidMount() {
    window.document.addEventListener(
      "updatePromoPreview",
      this.handleUpdatePromoPreview,
    );
    makeImagesResponsive();
  }

  componentWillUnmount() {
    window.document.removeEventListener(
      "updatePromoPreview",
      this.handleUpdatePromoPreview,
    );
  }

  componentDidUpdate() {
    makeImagesResponsive();
  }

  handleUpdatePromoPreview(e) {
    this.setState({
      promo: e.detail,
    });
  }

  getData() {
    return this.props.promo || this.state.promo || {};
  }

  renderPromotion() {
    const data = this.getData();
    const responsive_image_sizes = {
      ">992": "full",
      "<992": "992",
      "<768": "768",
      "<500": "500",
      "<380": "380",
    };

    if (data.design_option === "custom") {
      const customPromo = [data.promo_box_a, data.promo_box_bc].map((box) => {
        let srcset = [];

        if (box) {
          // Add the entry to the start so the order is smallest to largest
          Object.values(box).forEach((size, index) => {
            if (size) {
              // Add the entry to the start so the order is smallest to largest
              srcset.unshift(
                Object.keys(responsive_image_sizes)[index] + ":" + size.url,
              );
            } else if (srcset[0]) {
              // If the breakpoint doesn't have an image assigned to it, grab the next largest image assigned and add it to that breakpoint.
              let last = srcset[0];
              srcset.unshift(
                Object.keys(responsive_image_sizes)[index] +
                  ":" +
                  last.substr(5),
              );
            }
          });
        }

        return srcset;
      });

      return (
        <div className="jiffy-promotion-custom">
          {this.props.box === "a" && (
            <img
              src=""
              className="img-responsive"
              data-src={customPromo[0]}
              alt=""
            />
          )}
          {(this.props.box === "b" || this.props.box === "c") && (
            <img
              src=""
              className="img-responsive"
              data-src={customPromo[1]}
              alt=""
            />
          )}
        </div>
      );
    } else {
      const layout = data.layout || "default";

      // Overlay modifier from the layout map: '' | 'dark' | 'light'
      const overlayMap = {
        default: "",
        "1": "",
        "2": "dark",
        "3": "light",
      };
      const overlayMod = overlayMap[layout] || "";

      // Single merged element: __link + wrapper + layout + state classes
      const wrapperClasses = [
        "jiffy-promotion__link",
        "jiffy-promotion-wrapper",
        layout !== "default"
          ? `jiffy-promotion-wrapper--layout-${layout}`
          : `jiffy-promotion-wrapper--layout-${layout}`,
        data.tag_text ? "has-tag" : "",
        data.button_text ? "has-button" : "",
      ]
        .filter(Boolean)
        .join(" ");

      const bg = data.background_image && data.background_image.url;

      // Fixed logo URL (plugin constant equivalent for the preview)
      const promoLogoUrl = process.env.REACT_APP_PROMO_LOGO_URL || "";

      // Click target — only when link isn't disabled and we have a destination
      const permalink = data.external_site || data.permalink || "";
      const target = data.target || "_self";
      const linkEnabled = !data.disable_link && permalink !== "";

      return (
        <div
          className={wrapperClasses}
          style={bg ? { backgroundImage: `url(${bg})` } : undefined}
        >
          {/* Absolutely-positioned card link, emitted first */}
          {linkEnabled && (
            <a
              className="jiffy-promotion__card-link"
              href={permalink}
              target={target}
              title={`Learn more about ${data.post_title || ""}`}
              aria-label={`Learn more about ${data.post_title || ""}`}
            />
          )}

          {/* Overlay with layout-driven modifier */}
          <div className={`jiffy-promotion-overlay ${overlayMod}`.trim()} />

          {/* Fixed logo, gated on the constant URL */}
          {promoLogoUrl !== "" && (
            <div className="jiffy-promotion-logo jiffy-coupon__print__logo">
              <img src={promoLogoUrl} alt="Jiffy Lube" decoding="async" />
            </div>
          )}

          {/* Text block: tag + heading only (no subheading) */}
          {(data.tag_text || data.heading) && (
            <div className="jiffy-promotion-text">
              {data.tag_text && (
                <div className="jiffy-promotion-tag">
                  <span>{data.tag_text}</span>
                </div>
              )}
              {data.heading && (
                <div className="jiffy-promotion-heading">{data.heading}</div>
              )}
            </div>
          )}

          {/* CTA row — simplified button, no Fusion soup */}
          <div className="jiffy-promotion-cta-row">
            {data.button_text && (
              <a
                className="jl-button btn btn-download"
                href={linkEnabled ? permalink : undefined}
                target={target}
                rel={data.external_site ? "noopener" : undefined}
              >
                <span className="btn-text">{data.button_text}</span>
              </a>
            )}
          </div>

          {/* Disclaimer — un-gated by layout, only by presence */}
          {data.disclaimer && (
            <p className="jiffy-promotion-disclaimer">{data.disclaimer}</p>
          )}
        </div>
      );
    }
  }

  renderCoupon() {
    const data = this.getData();
    const date = new Date();

    return (
      <div className="container jiffy-coupon__container">
        <h1 className="jiffy-coupon__heading">
          {data.page_heading ? data.page_heading : data.discount}
        </h1>

        <div className="jiffy-coupon__print">
          <i className="icon icon-scissors"></i>
          <div className="row">
            <div className="jiffy-coupon__print__left">
              {(data.discount || data.description) && (
                <h2 className="jiffy-coupon__print__title">
                  {data.discount}
                  <small>{data.description}</small>
                </h2>
              )}
              <p className="jiffy-coupon__print__terms">
                <span className="jiffy-coupon__print__address">
                  {t("VALID AT PARTICIPATING LOCATIONS IN ONTARIO, CANADA")}
                </span>
              </p>
              {data.disclaimer && (
                <div className="jiffy-coupon__legals">
                  <p>{data.disclaimer}</p>
                </div>
              )}
            </div>
            <div className="jiffy-coupon__print__right">
              <div className="row">
                <div className="jiffy-coupon__print__fields">
                  {(data.promo_code || data.promo_code_mobile) && (
                    <div className="jiffy-coupon__print__field jiffy-coupon__print__field--promo-code">
                      <span className="jiffy-coupon__print__field-title">
                        {t("Promo Code")}:
                      </span>
                      <span className="jiffy-coupon__print__field-value">
                        {data.promo_code && data.promo_code_mobile ? (
                          <React.Fragment>
                            <span className="hidden-xs">{data.promo_code}</span>
                            <span className="visible-xs">
                              {data.promo_code_mobile}
                            </span>
                          </React.Fragment>
                        ) : (
                          data.promo_code || data.promo_code_mobile
                        )}
                      </span>
                    </div>
                  )}
                  {data.banner_colour && (
                    <div className="jiffy-coupon__print__field jiffy-coupon__print__field--banner-colour">
                      <hr
                        className={"banner_colour"}
                        style={{
                          color: data.banner_colour,
                          backgroundColor: data.banner_colour,
                        }}
                      />
                    </div>
                  )}
                  {data.coupon_expiry_type &&
                  (data.expiry_date || data.expiry_time_frame) ? (
                    <div className="jiffy-coupon__print__field jiffy-coupon__print__field--offer-expires">
                      <span className="jiffy-coupon__print__field-title">
                        {t("Offer Expires")}:
                      </span>
                      <span className="jiffy-coupon__print__field-value">
                        {data.coupon_expiry_type === "expiry-fixed"
                          ? data.expiry_date && (
                              <Moment format="MM/DD/YYYY">
                                {data.expiry_date}
                              </Moment>
                            )
                          : data.expiry_time_frame && (
                              <Moment
                                format="MM/DD/YYYY"
                                add={{
                                  [data.expiry_time_frame.variation +
                                  "s"]: parseInt(
                                    data.expiry_time_frame.increment,
                                  ),
                                }}
                              >
                                {date}
                              </Moment>
                            )}
                      </span>
                    </div>
                  ) : null}
                </div>
                {(data.promo_code || data.promo_code_mobile) && (
                  <div className="jiffy-coupon__print__barcode">
                    <img src={barcode} alt="Barcode" />
                  </div>
                )}
                <div className="jiffy-coupon__print__logo">
                  <img src={jiffylubelogo} width="175" alt="Jiffy Lube" />
                </div>
                <div className="jiffy-coupon__print__appointment">
                  {t("No Appointment!")}
                </div>
                <p className="jiffy-coupon__print__terms">
                  <span className="jiffy-coupon__print__address">
                    {t("VALID AT PARTICIPATING LOCATIONS IN ONTARIO, CANADA")}
                  </span>
                </p>
                {data.disclaimer && (
                  <div className="jiffy-coupon__legals">
                    <p>{data.disclaimer}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  renderAlert() {
    const data = this.getData();
    const innerAlert = (
      <div className="jiffy-alert__container">
        <strong>{data.alert_heading}</strong>&nbsp;
        {data.alert_subheading}&nbsp;
        {data.external_site && (
          <React.Fragment>
            {data.alert_link_action}
            <i className="icon icon-chevron-right"></i>
          </React.Fragment>
        )}
      </div>
    );

    return (
      <div
        className={classNames(
          "jiffy-alert",
          "jiffy-alert--" + data.alert_style,
        )}
      >
        {data.external_site ? (
          <a
            className="jiffy-alert__link"
            href={data.external_site}
            target="_blank"
            rel="noopener noreferrer"
          >
            {innerAlert}
          </a>
        ) : (
          innerAlert
        )}
      </div>
    );
  }

  render() {
    const data = this.getData();

    const showMockup = data.mockup !== "false";

    let promoDisplay = data._preview_coupon
      ? this.renderCoupon()
      : this.renderPromotion();

    let alertDisplay = this.renderAlert();

    return (
      <React.Fragment>
        {data.type === "alert" && alertDisplay}
        <SitePreview
          box={data._preview_coupon ? "a" : this.props.box}
          mockup={showMockup}
          className={this.props.className}
        >
          {data.type !== "alert" && (
            <div className="jiffy-promotion">
              {"event" === data.type && data.external_site ? (
                <a
                  className="jiffy-promotion__link"
                  href={data.external_site}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {promoDisplay}
                </a>
              ) : (
                promoDisplay
              )}
            </div>
          )}
        </SitePreview>
      </React.Fragment>
    );
  }
}

export { Preview };
